import React, {ReactNode} from 'react'
import {StyledDiv} from '../../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox} from './NatAdminApplicatioPrintCommonStyle'
import {RESIDENT_APPLY_PROCESS, RESIDENT_APPLY_STATUS} from '../../../../../../CodeMaster'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {NatSealField, SealField} from './NatSealField'
import {convertToRoomNumberForResident, getFormattedZipCode} from '../../../../../common/util/Natutil'
import {ApplyStatusInfo} from '../../../repository/NatGetApplicationStatus'
import moment from 'moment'
import {NatEncloseInParenthesis} from './NatEncloseInParenthesis'
import {NatConstructionApplyDetail} from '../../../repository/NatGetConstructionApply'

interface Props {
    constructionApplicationDocumentParam: NatConstructionApplyDetail
    applyStatusInfoList: ApplyStatusInfo[]
}

export const NatConstructionApplicationDocument: React.FC<Props> = props => {
    const {
        constructionApplicationDocumentParam,
        applyStatusInfoList
    } = props

    const {
        applyId,
        condominiumName,
        ridgeName,
        roomNumber,
        applicantFamilyName,
        applicantFirstName,
        constructionContentDetail,
        constructionStartDate,
        constructionEndDate,
        contractorName,
        contractorZipCode,
        contractorPrefecture,
        contractorAddress2,
        contractorAddress3,
        contractorBuildingRoomNumber,
        contractorPhoneNumber,
        contractorRep,
    } = constructionApplicationDocumentParam

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const applicantName = `${applicantFamilyName} ${applicantFirstName}`

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>工事申請書</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'3mm'}>
                        {renderSealField(applyStatusInfoList)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                　私は、専有部分の改修工事を実施したいので、次の通り申込をいたします。<br/>
                なお当該工事を原因として近隣住戸からクレームが生じた場合も自主的に解決し、<br/>
                後日異議の申し立てをいたしません。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>1  申請者</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('氏名', applicantName)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail(
                            '2  工事内容',
                            <Box whiteSpace={'pre-wrap'}>{constructionContentDetail}</Box>)
                        }
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>
                        3  工事期間
                    </Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('開始日', renderConstructionDate(constructionStartDate))}
                        {renderApplicationDetail('終了日', renderConstructionDate(constructionEndDate))}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>
                        4  工事施工者
                    </Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('業者名', contractorName)}
                        {renderApplicationDetail('郵便番号', getFormattedZipCode(contractorZipCode, false))}
                        {renderApplicationDetail('都道府県', contractorPrefecture)}
                        {renderApplicationDetail('市区町村', contractorAddress2)}
                        {renderApplicationDetail('番地', contractorAddress3)}
                        {renderApplicationDetail('建物名・部屋番号', contractorBuildingRoomNumber)}
                        {renderApplicationDetail('電話番号', contractorPhoneNumber)}
                        {renderApplicationDetail('担当者', contractorRep)}
                    </Box>
                </Box>
            </Box>
        </FirstPageDiv>
    </>)

    NatConstructionApplicationDocument.displayName = 'NatConstructionApplicationDocument'
}

const renderApplicationDetail = (title: string, value: string | ReactNode) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value}/>
        </PrintApplicationDetailContainer>
    )
}

const renderConstructionDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日
        </>
    )
}

const renderSealField = (applyStatusInfoList: ApplyStatusInfo[]) => {
    const sealFields: SealField[] = applyStatusInfoList.map((info) => {
        const applyStatusEnum = RESIDENT_APPLY_PROCESS.codeToEnumConstants(info.applyProcess)
        let seal
        if (info.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
            seal = '却下'
        } else {
            seal = info.actual.registerDatetime ? moment(info.actual.registerDatetime).format('YYYY/M/D') : ''
        }
        return {
            applyStatus: applyStatusEnum.label,
            seal: seal,
            sortOrder: applyStatusEnum.displayOrder
        }
    })

    return (
        <NatSealField sealFields={sealFields}/>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`
